import React, { useState, useMemo, useEffect } from 'react'
import { Button } from 'antd'
import classnames from 'classnames'
import ContentHeader from '../ContentHeader'
import { useI18n, usePersonalInfo, useProfile } from 'contexts/AuthContext'
import CareInput from 'components/CareInput'
import { ThreeOptionSelect } from 'components/SwitchSelect/index'
import { GenderOptions } from '../CompleteProfile'
import BirthdayInput from 'components/BirthdayInput/index'
import notEligibleIcon from '../img/ic-not-eligible.svg'
import mbiCardImage from '../img/image-mbi-card.png'
import classes from './mbiNotFound.module.scss'
import { useMCA } from './useMCA'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getMbiFailedInfo } from './mbiFailedCache'
import { ScreenKey } from '../util'

export const formatMedicareBeneficiaryId = (value) => {
  const raw = value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 11)

  if (raw.length <= 4) {
    return raw
  }
  if (raw.length <= 7) {
    return `${raw.slice(0, 4)}-${raw.slice(4)}`
  }
  return `${raw.slice(0, 4)}-${raw.slice(4, 7)}-${raw.slice(7)}`
}

export const getMedicareBeneficiaryIdRaw = (value) =>
  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

const MBINotFound = (props) => {
  const { i18n } = useI18n()
  const { sku, setCurrentKey } = props
  const { personalInfo } = usePersonalInfo()
  const { profile } = useProfile()
  const [firstName, setFirstName] = useState(
    personalInfo.firstNameForMca || personalInfo.firstName
  )
  const [lastName, setLastName] = useState(personalInfo.lastName)
  const [birthday, setBirthday] = useState(profile.birthday)
  const [gender, setGender] = useState(profile.gender || 'Female')
  const [medicareBeneficiaryId, setMedicareBeneficiaryId] = useState('')
  const [medicareBeneficiaryIdOnFocus, setMedicareBeneficiaryIdOnFocus] =
    useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showMbiError, setShowMbiError] = useState(true)

  const onBack = (e) => {
    props.onBack(e)
  }
  const { resubmitMbi } = useMCA({ sku })
  const { mbiNotFoundReason, mbiNotFoundAttemptNumber } = getMbiFailedInfo()

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - MBI Not Found', {
      sku,
      reason: mbiNotFoundReason,
      attempt_numbers: mbiNotFoundAttemptNumber || 1,
    })
  }, [sku, mbiNotFoundReason, mbiNotFoundAttemptNumber])

  const onNext = async () => {
    setShowMbiError(false)
    logAmplitudeEvent('Click: Onboarding - MBI Not Found - Resubmit', {
      sku,
      attempt_numbers: mbiNotFoundAttemptNumber || 1,
    })
    setIsSubmitting(true)
    const nextScreenKey = await resubmitMbi({
      firstName,
      lastName,
      birthday,
      gender,
      medicareBeneficiaryId: medicareBeneficiaryId.replace(/-/g, ''),
    })
    if (nextScreenKey !== ScreenKey.MCA_MBI_NOT_FOUND) {
      setCurrentKey(nextScreenKey)
    } else {
      logAmplitudeEvent('Error: Onboarding - MBI - Format Invalid', { sku })
      setShowMbiError(true)
    }
    setIsSubmitting(false)
  }

  const isNextButtonEnable = useMemo(() => {
    return firstName && lastName && gender && birthday && medicareBeneficiaryId
  }, [firstName, lastName, gender, birthday, medicareBeneficiaryId])

  const isMedicareBeneficiaryIdValid = useMemo(() => {
    return getMedicareBeneficiaryIdRaw(medicareBeneficiaryId).length === 11
  }, [medicareBeneficiaryId])

  const isInputValid = useMemo(() => {
    return (
      (!medicareBeneficiaryId ||
        medicareBeneficiaryIdOnFocus ||
        isMedicareBeneficiaryIdValid) &&
      !showMbiError
    )
  }, [
    medicareBeneficiaryId,
    medicareBeneficiaryIdOnFocus,
    isMedicareBeneficiaryIdValid,
    showMbiError,
  ])

  const errorMessage = useMemo(() => {
    const rawMedicareBeneficiaryId = getMedicareBeneficiaryIdRaw(
      medicareBeneficiaryId
    )
    if (
      rawMedicareBeneficiaryId.length < 11 &&
      rawMedicareBeneficiaryId.length > 0
    ) {
      return i18n('Please enter all 11 characters.')
    }
    return i18n('Medicare Number not found')
  }, [medicareBeneficiaryId, i18n])

  return (
    <div className={classnames(classes.styles, 'info-sec')}>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <img src={notEligibleIcon} alt='' className='top-icon' />
        <div className='title-pns'>
          {i18n('Sorry, we were not able to find you')}
        </div>
        <div
          className='subtitle'
          style={{ marginBottom: '10px', marginTop: '17px' }}
        >
          {i18n('Please verify your information and try again')}
        </div>
        <div className='name-sec'>
          <div className='mbi-not-found-item'>
            <div className='item-label'>{i18n('First Name')}</div>
            <CareInput
              {...{
                inputValue: firstName,
                setInputValue: setFirstName,
                placeholder: i18n(`First Name`),
              }}
            />
          </div>
          <div className='mbi-not-found-item'>
            <div className='item-label'>{i18n('Last Name')}</div>
            <CareInput
              {...{
                inputValue: lastName,
                setInputValue: setLastName,
                placeholder: i18n(`Last Name`),
              }}
            />
          </div>
        </div>
        <div className='mbi-not-found-item'>
          <div className='item-label'>{i18n('Gender')}</div>
          <ThreeOptionSelect
            className='item-right'
            options={GenderOptions}
            value={gender}
            onChange={(value) => {
              setGender(value)
            }}
          />
        </div>
        <div className='mbi-not-found-item'>
          <div className='item-label'>{i18n('Birthday')}</div>
          <BirthdayInput birthday={birthday} setBirthday={setBirthday} />
        </div>
        <div className='mbi-not-found-item'>
          <div className='item-label'>{i18n('Medicare Number')}</div>
          <CareInput
            {...{
              inputValue: medicareBeneficiaryId,
              setInputValue: (value) => {
                setShowMbiError(false)
                setMedicareBeneficiaryId(formatMedicareBeneficiaryId(value))
              },
              placeholder: '',
              isValid: isInputValid,
              errorMessage: errorMessage,
              className: 'item-right',
              type: 'text',
              maxLength: 13,
              onFocus: () => setMedicareBeneficiaryIdOnFocus(true),
              onBlur: () => setMedicareBeneficiaryIdOnFocus(false),
            }}
          />
        </div>
        <img src={mbiCardImage} alt='' className='mbi-card-image' />
        <div className='mbi-not-found-card-text'>
          <span className='text-bold'>{i18n('Not sure where to look?')}</span>
          {i18n(
            'Your Medicare Number is the 11-character combination of letters and numbers on the front of your Medicare card.'
          )}
        </div>
        <div className='mbi-not-found-card-text'>
          <span className='text-bold'>
            {i18n('Don’t have your Medicare card?')}
          </span>
          {i18n(
            'Find your Medicare Number on {0}',
            <a
              href='https://www.medicare.gov/account/login'
              target='_blank'
              rel='noreferrer'
            >
              www.medicare.gov
            </a>
          )}
        </div>
        <Button
          className='btn-primary full-width-btn'
          style={{ marginTop: '24px', width: '100%' }}
          onClick={onNext}
          disabled={!isNextButtonEnable}
          loading={isSubmitting}
        >
          {i18n('Next')}
        </Button>
      </div>
    </div>
  )
}

export default MBINotFound
